import React, { Dispatch, useEffect, useState } from 'react'
import { Icon, notification, Switch } from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { AssignTree } from 'src/types'
import { Button, Table } from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import { getTree } from 'src/core/store/ducks/tree/actions'
import { NodeColor } from 'src/components/Tree/Nodes'
import { useT } from 'src/core/hooks/use-translation'
import * as PoliciesTreeService from 'src/services/api/policies-trees'

import * as CSS from './styles'

import { PropsTreeSelected } from '../..'
import HierarchyTreeSelectModal from '../hierarchy-tree-select-modal'

type HierarchyFlowsChainedProps = {
  treesSelected: PropsTreeSelected
  setTreeSelected: Dispatch<HierarchyFlowsChainedProps['treesSelected']>
}

export type SelectNodeProps = Pick<
  AssignTree,
  'assign_tree_id' | 'assign_tree_name' | 'color' | 'disabled'
> & {
  policy_name?: 'approval' | 'conference'
}

export default function HierarchyFlowsChained({
  setTreeSelected,
  treesSelected
}: HierarchyFlowsChainedProps) {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  const dispatch = useDispatch()

  const { t } = useT()
  const [loading, setLoading] = useState(false)
  const [dataChainedApprovals, setDataChainedApprovals] = useState<
    PoliciesTreeService.ChainsProps[]
  >([])
  const [dataChainedConferences, setDataChainedConferences] = useState<
    PoliciesTreeService.ChainsProps[]
  >([])
  const [selectedNode, setSelectedNode] = useState<SelectNodeProps[]>([])

  function updateSelectedNode(
    nodes?: PoliciesTreeService.ChainsProps[],
    policy_name?: 'approval' | 'conference'
  ): void {
    if (!nodes) {
      setSelectedNode([])
      return
    }

    setSelectedNode([
      {
        assign_tree_id: treesSelected.id,
        assign_tree_name: treesSelected.name,
        color: NodeColor.DISABLED,
        disabled: true,
        policy_name
      },
      ...nodes.map(node => ({
        ...node,
        assign_tree_id: node.chained_assign_tree_id,
        assign_tree_name: node.chained_assign_tree_name,
        color: NodeColor.SELECTED
      }))
    ])
  }

  function getChainedApprovals() {
    PoliciesTreeService.getChains({
      assign_tree_id: treesSelected.id,
      policy_name: 'approval'
    })
      .then(response => {
        if (response.status === 200) {
          const chained: PoliciesTreeService.ChainsProps[] =
            response.data?.map(data => ({
              ...data,
              policy_name: 'approval'
            })) || []
          setDataChainedApprovals(chained)
          if (selectedNode.length) {
            updateSelectedNode(response.data, 'approval')
          }
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function getChainedConference() {
    PoliciesTreeService.getChains({
      assign_tree_id: treesSelected.id,
      policy_name: 'conference'
    })
      .then(response => {
        if (response.status === 200) {
          const chained: PoliciesTreeService.ChainsProps[] =
            response.data?.map(data => ({
              ...data,
              policy_name: 'conference'
            })) || []
          setDataChainedConferences(chained)
          if (selectedNode.length) {
            updateSelectedNode(response.data, 'approval')
          }
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const columns: ColumnProps<any>[] = [
    {
      title: 'web.tree.structures.table.structure',
      dataIndex: 'chained_assign_tree_name',
      key: 'structure'
    },
    {
      title: 'web.tree.structures.table.visit-structure',
      dataIndex: 'visit-structure',
      key: 'visit-structure',
      align: 'center',
      render: (_: unknown, row: any) => (
        <Icon
          type="arrow-up"
          rotate={45}
          onClick={() => {
            document.getElementById(String(row.chained_assign_tree_id)).click()
            window.scrollTo(0, 0)
          }}
        />
      )
    },
    {
      align: 'center',
      key: 'remove',
      render: (_: unknown, row: any) => (
        <Icon
          type="disconnect"
          onClick={() =>
            PoliciesTreeService.saveChained({
              assign_tree_id: treesSelected.id,
              policy_name: row.policy_name,
              chained_assign_tree_id: row.chained_assign_tree_id,
              method: 'remove'
            }).then(() =>
              row.policy_name === 'approval'
                ? getChainedApprovals()
                : getChainedConference()
            )
          }
        />
      ),
      title: 'web.tree.structures.table.remove'
    }
  ]

  useEffect(() => {
    setLoading(true)
    getChainedApprovals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treesSelected])

  useEffect(() => {
    setLoading(true)
    getChainedConference()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treesSelected])

  return (
    <>
      <CSS.Content>
        <CSS.Text>{t('web.tree.structures.chained-enable')}</CSS.Text>
        <Switch
          size="default"
          disabled={false}
          checked={treesSelected.chained}
          onChange={() =>
            PoliciesTreeService.updateChained({
              assign_tree_id: treesSelected.id,
              chained: !treesSelected.chained,
              p_assign_tree_type: treesSelected.p_assign_tree_type,
              assign_tree_name: treesSelected.name,
              organization_id: me.organization.organization_id
            }).then(response => {
              dispatch(getTree())
              setTreeSelected({
                ...treesSelected,
                chained: response.data[0].chained
              })
            })
          }
        />
      </CSS.Content>

      {treesSelected.chained && (
        <>
          <CSS.Div>
            <CSS.Text>{t('web.tree.structures.chained-approval')}</CSS.Text>
            <CSS.Div>
              <Table
                dataSource={dataChainedApprovals}
                loading={loading}
                className="table-striped"
                columns={columns}
                size="small"
              />
            </CSS.Div>
          </CSS.Div>
          <CSS.Button>
            <Button
              type="primary"
              onClick={() => {
                updateSelectedNode(dataChainedApprovals, 'approval')
              }}
            >
              <Icon type="link" />
              {t('web.tree.structures.chain')}
            </Button>
          </CSS.Button>
          <CSS.Div>
            <CSS.Text>{t('web.tree.structures.chained-conference')}</CSS.Text>
            <CSS.Div>
              <Table
                dataSource={dataChainedConferences}
                loading={loading}
                className="table-striped"
                columns={columns}
                size="small"
              />
            </CSS.Div>
          </CSS.Div>

          <CSS.Button>
            <Button
              type="primary"
              onClick={() => {
                updateSelectedNode(dataChainedConferences, 'conference')
              }}
            >
              <Icon type="link" />
              {t('web.tree.structures.chain')}
            </Button>
          </CSS.Button>
        </>
      )}
      <HierarchyTreeSelectModal
        toggle={updateSelectedNode}
        valuedNodes={selectedNode}
        refetch={(policy_name: 'approval' | 'conference') =>
          policy_name === 'approval'
            ? getChainedApprovals()
            : getChainedConference()
        }
      />
    </>
  )
}
